import { useEffect, useId } from 'react';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  confirming?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  open,
  title,
  children,
  confirmLabel,
  confirming,
  onCancel,
  onConfirm,
}: ConfirmModalProps): React.ReactElement | null {
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onCancel();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4 py-6 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-full w-full max-w-sm overflow-auto rounded-2xl border border-line bg-paper-soft p-6 shadow-[0_30px_80px_-30px_rgba(31,27,21,0.6)]"
      >
        <h2 id={titleId} className="font-serif text-2xl italic leading-tight text-ink">
          {title}
        </h2>
        <div className="mt-4 text-sm leading-6 text-ink-soft">{children}</div>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={confirming}
            onClick={onCancel}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={confirming}
            onClick={onConfirm}
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-clay px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(181,82,58,0.85)] transition hover:bg-clay/85 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {confirming ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
